import * as React from 'react';

import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensors,
  useSensor,
} from '@dnd-kit/core';

import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { Box, LinearProgress, Paper, Typography } from '@mui/material';
import { useUnattachedEventReportsData } from '../../core/context/UnattachedEventReportsContext';
import { EventReport } from '../../core/api/clients';
import SortableEventReport from './SortableEventReport';

interface UnattachedEventReportsWrapperProps {
  children: React.ReactNode;
}

function UnattachedEventReportsWrapper({ children }: UnattachedEventReportsWrapperProps) {
  const { getReport, moveReport, isLoading, hasReports } = useUnattachedEventReportsData();

  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);
  const [activeReport, setActiveReport] = React.useState<EventReport | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    const { current } = active.data;
    if (current?.sortable?.containerId) {
      const eventReport = getReport(current.sortable.containerId, active.id);
      if (eventReport) {
        setActiveId(active.id);
        setActiveReport(eventReport);
      }
    }
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    const activeContainerId: UniqueIdentifier | null = active.data.current?.sortable?.containerId;
    const overContainerId: UniqueIdentifier | null = over?.data.current?.sortable?.containerId;

    if (activeContainerId && overContainerId && activeContainerId !== overContainerId) {
      moveReport(active.id, activeContainerId, overContainerId, over?.id);
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const activeContainerId: UniqueIdentifier | null = active.data.current?.sortable?.containerId;
    const overContainerId: UniqueIdentifier | null = over?.data.current?.sortable?.containerId;

    if (activeContainerId && overContainerId && activeContainerId !== overContainerId) {
      moveReport(active.id, activeContainerId, overContainerId, over?.id);
      setActiveReport(null);
      setActiveId(null);
    }
  };

  if (!isLoading && !hasReports) {
    return (
      <Box textAlign="center" padding={2}>
        <Typography fontSize={16} variant="body2">You are all caught up.</Typography>
      </Box>
    );
  }
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {isLoading ? (<LinearProgress />) : children}
      <DragOverlay>
        {activeReport ? (
          <Paper>
            <SortableEventReport eventReport={activeReport} parentContainerId={null} />
          </Paper>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default UnattachedEventReportsWrapper;
